import React, { useState, useEffect } from 'react';
import { X, Heart, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DonationModal = ({ isOpen, onClose }) => {
  const [packages, setPackages] = useState([]);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [customAmount, setCustomAmount] = useState('');
  const [loading, setLoading] = useState(false);
  const [donorInfo, setDonorInfo] = useState({ name: '', email: '' });

  useEffect(() => {
    if (isOpen) {
      fetchPackages();
    }
  }, [isOpen]);

  // Check for return from Stripe
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');
    
    if (sessionId && window.location.pathname === '/donation-success') {
      pollPaymentStatus(sessionId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchPackages = async () => {
    try {
      const response = await axios.get(`${API}/donate/packages`);
      setPackages(response.data.packages);
    } catch (error) {
      console.error('Error fetching donation packages:', error);
    }
  };

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      toast.error('Payment status check timed out. Please check your email for confirmation.');
      return;
    }

    try {
      const response = await axios.get(`${API}/donate/status/${sessionId}`);
      
      if (response.data.payment_status === 'paid') {
        toast.success('🎉 Thank you for your generous donation! Your support makes a real difference.');
        // Clean URL
        window.history.replaceState({}, '', window.location.pathname);
        return;
      } else if (response.data.status === 'expired') {
        toast.error('Payment session expired. Please try again.');
        return;
      }

      // Continue polling
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      console.error('Error checking payment status:', error);
      toast.error('Error checking payment status. Please contact us if you made a donation.');
    }
  };

  const handleDonate = async () => {
    // Validation
    if (!selectedPackage && !customAmount) {
      toast.error('Please select a donation amount or enter a custom amount');
      return;
    }

    if (customAmount && parseFloat(customAmount) < 5) {
      toast.error('Custom donation must be at least $5.00');
      return;
    }

    setLoading(true);

    try {
      const originUrl = window.location.origin;
      
      const requestBody = {
        origin_url: originUrl,
        donor_name: donorInfo.name || undefined,
        donor_email: donorInfo.email || undefined
      };

      if (selectedPackage) {
        requestBody.package_id = selectedPackage;
      } else {
        requestBody.custom_amount = parseFloat(customAmount);
      }

      const response = await axios.post(`${API}/donate/create-session`, requestBody);
      
      // Redirect to Stripe Checkout
      if (response.data.url) {
        window.location.href = response.data.url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (error) {
      console.error('Donation error:', error);
      const errorMessage = error.response?.data?.detail || 'Unable to process donation. Please try again.';
      toast.error(errorMessage);
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto relative">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 rounded-full hover:bg-slate-100 transition-colors"
        >
          <X className="w-5 h-5" />
        </button>

        <CardHeader className="text-center pb-4">
          <div className="w-16 h-16 rounded-full bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center mx-auto mb-4">
            <Heart className="w-8 h-8 text-white" />
          </div>
          <CardTitle className="text-3xl">Support Our Mission</CardTitle>
          <CardDescription className="text-base">
            Your donation helps displaced Ukrainian families achieve legal stability and economic independence
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-6">
          {/* Donation Packages */}
          <div>
            <h3 className="font-semibold text-lg mb-4">Choose an amount:</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {packages.map((pkg) => (
                <button
                  key={pkg.id}
                  onClick={() => {
                    setSelectedPackage(pkg.id);
                    setCustomAmount('');
                  }}
                  className={`p-4 rounded-xl border-2 transition-all text-left ${
                    selectedPackage === pkg.id
                      ? 'border-emerald-600 bg-emerald-50 shadow-md'
                      : 'border-slate-200 hover:border-emerald-300 hover:bg-slate-50'
                  }`}
                >
                  <div className="text-2xl font-bold text-emerald-600">
                    ${pkg.amount}
                  </div>
                  <div className="text-sm font-semibold text-slate-900 mt-1">
                    {pkg.label}
                  </div>
                  <div className="text-xs text-slate-600 mt-1">
                    {pkg.description}
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Custom Amount */}
          <div>
            <h3 className="font-semibold text-base mb-2">Or enter a custom amount:</h3>
            <div className="flex items-center gap-2">
              <span className="text-2xl font-bold text-slate-700">$</span>
              <Input
                type="number"
                min="5"
                step="1"
                placeholder="Enter amount (min $5)"
                value={customAmount}
                onChange={(e) => {
                  setCustomAmount(e.target.value);
                  setSelectedPackage(null);
                }}
                className="text-lg h-12"
              />
            </div>
            <p className="text-xs text-slate-500 mt-2">Minimum donation: $5.00</p>
          </div>

          {/* Optional Donor Info */}
          <div className="space-y-3 pt-4 border-t">
            <h3 className="font-semibold text-base">Your information (optional):</h3>
            <Input
              type="text"
              placeholder="Your name"
              value={donorInfo.name}
              onChange={(e) => setDonorInfo({...donorInfo, name: e.target.value})}
              className="h-11"
            />
            <Input
              type="email"
              placeholder="Your email (for receipt)"
              value={donorInfo.email}
              onChange={(e) => setDonorInfo({...donorInfo, email: e.target.value})}
              className="h-11"
            />
          </div>

          {/* Donate Button */}
          <Button
            onClick={handleDonate}
            disabled={loading}
            className="w-full h-14 text-lg bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white shadow-lg"
          >
            {loading ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                Processing...
              </>
            ) : (
              <>
                Continue to Secure Payment
                <Heart className="w-5 h-5 ml-2" />
              </>
            )}
          </Button>

          <p className="text-xs text-center text-slate-500">
            Secure payment processed by Stripe. Svetlana Foundation is a 501(c)(3) nonprofit organization.
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default DonationModal;
